import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  BookOpen,
  Plus,
  Search,
  Sparkles,
  FileText,
  TrendingUp,
  Share2,
  Download,
  BarChart3,
  Eye,
  CheckCircle,
  Clock
} from 'lucide-react';

interface BlogSeries {
  id: string;
  title: string;
  description: string;
  total_parts: number;
  target_audience: string;
  tone: string;
  series_type: 'series' | 'standalone';
  status: string;
  created_at: string;
}

interface BlogPost {
  id: string;
  series_id: string;
  part_number: number;
  title: string;
  content: string;
  excerpt: string;
  status: string;
  estimated_read_time: number;
  humanization_score: number;
  created_at: string;
}

export default function BlogSeries() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  const [series, setSeries] = useState<BlogSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [topicInput, setTopicInput] = useState('');
  const [researchLoading, setResearchLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [researchData, setResearchData] = useState<any>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);

  const [seriesType, setSeriesType] = useState<'series' | 'standalone'>('series');
  const [totalParts, setTotalParts] = useState(5);
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState('professional');
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (user && activeTab === 'manage') {
      loadSeries();
    }
  }, [user, activeTab]);

  const loadSeries = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_series')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSeries(data || []);
    } catch (error) {
      console.error('Error loading series:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResearchTopic = async () => {
    if (!topicInput.trim()) return;

    setResearchLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/research-blog-topic`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            topicInput,
            targetAudience,
            generateSuggestions: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to research topic');
      }

      const result = await response.json();
      setSuggestions(result.suggestions || []);
      setResearchData(result.researchData || {});
    } catch (error) {
      console.error('Error researching topic:', error);
      alert('Failed to research topic. Please try again.');
    } finally {
      setResearchLoading(false);
    }
  };

  const handleGenerateSeries = async () => {
    if (!topicInput.trim() || !targetAudience.trim()) {
      alert('Please provide a topic and target audience');
      return;
    }

    setGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-blog-series`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            topic: selectedSuggestion?.title || topicInput,
            seriesType,
            totalParts: seriesType === 'series' ? totalParts : 1,
            targetAudience,
            tone,
            researchData,
            description,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate series');
      }

      const result = await response.json();
      alert(`Successfully generated ${result.posts.length} blog post(s)!`);

      setTopicInput('');
      setSuggestions([]);
      setResearchData(null);
      setSelectedSuggestion(null);
      setDescription('');
      setShowCreateModal(false);
      setActiveTab('manage');
      loadSeries();
    } catch (error) {
      console.error('Error generating series:', error);
      alert('Failed to generate series. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                <BookOpen className="text-emerald-600" />
                Blog Series Studio
              </h1>
              <p className="text-slate-600 mt-2">
                Create engaging, humanized blog series with AI-powered research and generation
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl"
            >
              <Plus size={20} />
              New Series
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === 'create'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Sparkles size={20} />
                Quick Start
              </div>
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === 'manage'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FileText size={20} />
                Manage Series
              </div>
            </button>
          </div>

          {activeTab === 'create' && (
            <div className="p-8">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Get started with AI-powered topic research
                  </h2>
                  <p className="text-slate-600">
                    Enter a topic or area of interest, and we'll help you discover engaging angles
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      What would you like to write about?
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={topicInput}
                        onChange={(e) => setTopicInput(e.target.value)}
                        placeholder="e.g., AI in healthcare, sustainable investing, remote team management..."
                        className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <button
                        onClick={handleResearchTopic}
                        disabled={researchLoading || !topicInput.trim()}
                        className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {researchLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                            Researching...
                          </>
                        ) : (
                          <>
                            <Search size={20} />
                            Research
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {suggestions.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <TrendingUp className="text-emerald-600" size={20} />
                        AI-Suggested Topics
                      </h3>
                      <div className="grid gap-4">
                        {suggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => setSelectedSuggestion(suggestion)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedSuggestion?.title === suggestion.title
                                ? 'border-emerald-600 bg-emerald-50'
                                : 'border-slate-200 hover:border-emerald-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-900 mb-1">
                                  {suggestion.title}
                                </h4>
                                <p className="text-sm text-slate-600 mb-2">
                                  {suggestion.rationale}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <FileText size={14} />
                                    {suggestion.estimatedParts} parts
                                  </span>
                                </div>
                              </div>
                              {selectedSuggestion?.title === suggestion.title && (
                                <CheckCircle className="text-emerald-600 flex-shrink-0" size={24} />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="w-full bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                      >
                        Continue with Selected Topic
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'manage' && (
            <div className="p-8">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent" />
                </div>
              ) : series.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="mx-auto text-slate-400 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    No series yet
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Create your first blog series to get started
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Create Series
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {series.map((s) => (
                    <SeriesCard key={s.id} series={s} onUpdate={loadSeries} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateSeriesModal
          topicInput={topicInput}
          setTopicInput={setTopicInput}
          selectedSuggestion={selectedSuggestion}
          seriesType={seriesType}
          setSeriesType={setSeriesType}
          totalParts={totalParts}
          setTotalParts={setTotalParts}
          targetAudience={targetAudience}
          setTargetAudience={setTargetAudience}
          tone={tone}
          setTone={setTone}
          description={description}
          setDescription={setDescription}
          generating={generating}
          onGenerate={handleGenerateSeries}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}

function CreateSeriesModal({
  topicInput,
  setTopicInput,
  selectedSuggestion,
  seriesType,
  setSeriesType,
  totalParts,
  setTotalParts,
  targetAudience,
  setTargetAudience,
  tone,
  setTone,
  description,
  setDescription,
  generating,
  onGenerate,
  onClose,
}: any) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            Configure Your Blog Series
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Topic / Title
            </label>
            <input
              type="text"
              value={selectedSuggestion?.title || topicInput}
              onChange={(e) => setTopicInput(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Enter your topic"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Series Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSeriesType('series')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  seriesType === 'series'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-300'
                }`}
              >
                <FileText className="mx-auto mb-2 text-emerald-600" size={24} />
                <div className="font-semibold">Multi-Part Series</div>
                <div className="text-xs text-slate-600 mt-1">
                  3-7 interconnected posts
                </div>
              </button>
              <button
                onClick={() => setSeriesType('standalone')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  seriesType === 'standalone'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-300'
                }`}
              >
                <BookOpen className="mx-auto mb-2 text-emerald-600" size={24} />
                <div className="font-semibold">Standalone Post</div>
                <div className="text-xs text-slate-600 mt-1">
                  Single comprehensive article
                </div>
              </button>
            </div>
          </div>

          {seriesType === 'series' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Number of Parts ({totalParts})
              </label>
              <input
                type="range"
                min="3"
                max="7"
                value={totalParts}
                onChange={(e) => setTotalParts(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>3 parts</span>
                <span>7 parts</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Target Audience
            </label>
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="e.g., Small business owners, Tech professionals, Marketing managers..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Writing Tone
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="professional">Professional</option>
              <option value="conversational">Conversational</option>
              <option value="authoritative">Authoritative</option>
              <option value="friendly">Friendly</option>
              <option value="educational">Educational</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              rows={3}
              placeholder="Add additional context or specific requirements..."
            />
          </div>
        </div>

        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={generating}
            className="px-6 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onGenerate}
            disabled={generating || !topicInput.trim() || !targetAudience.trim()}
            className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Generate Series
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function SeriesCard({ series, onUpdate: _onUpdate }: { series: BlogSeries; onUpdate: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const loadPosts = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    setLoadingPosts(true);
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('series_id', series.id)
        .order('part_number', { ascending: true });

      if (error) throw error;
      setPosts(data || []);
      setExpanded(true);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div
        onClick={loadPosts}
        className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-slate-900">
                {series.title}
              </h3>
              <span className={`px-2 py-1 text-xs rounded-full ${
                series.status === 'completed' ? 'bg-green-100 text-green-700' :
                series.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {series.status}
              </span>
              <span className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded-full">
                {series.series_type === 'series' ? `${series.total_parts} parts` : 'Standalone'}
              </span>
            </div>
            {series.description && (
              <p className="text-slate-600 text-sm mb-3">
                {series.description}
              </p>
            )}
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>Audience: {series.target_audience}</span>
              <span>Tone: {series.tone}</span>
              <span>Created: {new Date(series.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="ml-4">
            {loadingPosts ? (
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent" />
            ) : (
              <Eye className="text-slate-400" size={20} />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-6">
          <h4 className="font-semibold text-slate-900 mb-4">Posts in this series:</h4>
          <div className="space-y-3">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PostCard({ post }: { post: BlogPost }) {
  const [showContent, setShowContent] = useState(false);
  const [showDistribution, setShowDistribution] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(post.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportMarkdown = () => {
    const markdown = `# ${post.title}\n\n${post.content}`;
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${post.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-slate-500">
              Part {post.part_number}
            </span>
            <h5 className="font-semibold text-slate-900">
              {post.title}
            </h5>
          </div>
          <p className="text-sm text-slate-600 mb-2">
            {post.excerpt}
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {post.estimated_read_time} min read
            </span>
            <span className="flex items-center gap-1">
              <BarChart3 size={12} />
              Human score: {post.humanization_score}/10
            </span>
          </div>
        </div>
        <div className="ml-4 flex items-center gap-2">
          <button
            onClick={() => setShowExport(!showExport)}
            className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
            title="Export"
          >
            <Download size={18} />
          </button>
          <button
            onClick={() => setShowDistribution(!showDistribution)}
            className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
            title="Track Distribution"
          >
            <Share2 size={18} />
          </button>
          <button
            onClick={() => setShowContent(!showContent)}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
          >
            <Eye size={18} />
          </button>
        </div>
      </div>

      {showExport && (
        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <h6 className="text-sm font-semibold text-slate-900 mb-2">Export Options</h6>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-sm transition-colors"
            >
              {copied ? <CheckCircle size={16} className="text-green-600" /> : <FileText size={16} />}
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
            <button
              onClick={handleExportMarkdown}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-sm transition-colors"
            >
              <Download size={16} />
              Download as Markdown
            </button>
          </div>
        </div>
      )}

      {showDistribution && (
        <DistributionTracker postId={post.id} onClose={() => setShowDistribution(false)} />
      )}

      {showContent && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-slate-700">
              {post.content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DistributionTracker({ postId, onClose }: { postId: string; onClose: () => void }) {
  const { user } = useAuth();
  const [platform, setPlatform] = useState('linkedin_group');
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');
  const [distributions, setDistributions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDistributions();
  }, []);

  const loadDistributions = async () => {
    try {
      const { data, error } = await supabase
        .from('distribution_tracking')
        .select('*')
        .eq('post_id', postId)
        .order('shared_at', { ascending: false });

      if (error) throw error;
      setDistributions(data || []);
    } catch (error) {
      console.error('Error loading distributions:', error);
    }
  };

  const handleAddDistribution = async () => {
    if (!destination.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('distribution_tracking')
        .insert({
          post_id: postId,
          user_id: user?.id,
          platform,
          destination,
          notes,
          shared_at: new Date().toISOString(),
        });

      if (error) throw error;

      setDestination('');
      setNotes('');
      loadDistributions();
    } catch (error) {
      console.error('Error adding distribution:', error);
      alert('Failed to track distribution');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <h6 className="text-sm font-semibold text-slate-900">Distribution Tracking</h6>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <span className="text-xl">&times;</span>
        </button>
      </div>

      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="linkedin_group">LinkedIn Group</option>
              <option value="linkedin_profile">LinkedIn Profile</option>
              <option value="medium">Medium</option>
              <option value="substack">Substack</option>
              <option value="personal_blog">Personal Blog</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Destination</label>
            <input
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Group/page name"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Notes (Optional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes..."
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <button
          onClick={handleAddDistribution}
          disabled={loading || !destination.trim()}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          <Share2 size={16} />
          Track Distribution
        </button>
      </div>

      {distributions.length > 0 && (
        <div className="border-t border-slate-300 pt-3">
          <h6 className="text-xs font-semibold text-slate-700 mb-2">Distribution History</h6>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {distributions.map((dist) => (
              <div key={dist.id} className="bg-white p-2 rounded border border-slate-200 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-slate-900">{dist.destination}</span>
                  <span className="text-slate-500">
                    {new Date(dist.shared_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-slate-600">
                  Platform: {dist.platform.replace(/_/g, ' ')}
                </div>
                {dist.notes && (
                  <div className="text-slate-500 mt-1">{dist.notes}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
